/**
 * Desktop config rail — back to work, then the phone settings hub.
 *
 * `/account` and `/seats` are billing screens, not work tabs, so their left
 * menu is this list rather than Início / Rotinas. Settings uses the same
 * rail so opening Profile from Conta does not swap the chrome back to work.
 */
import { useTranslation } from 'react-i18next';
import {
  arrowBackOutline,
  card,
  helpCircle,
  key,
  language,
  mail,
  people,
  personCircle,
  school,
} from 'ionicons/icons';
import IonIcon from '../../components/IonIcon';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '../../components/ui/sidebar';
import { isMyWebHost } from '../../config/webOrigins';
import { HOME_PATH } from '../../components/useHasHome';
import { SUPPORT_EMAIL, getHelpUrl } from '../../constants/legalUrls';
import {
  SETTINGS_GROUPS,
  SETTINGS_SECTION_DEFS,
  SETTINGS_SECTIONS,
  settingsPath,
  type SettingsSection,
} from '../settings/sections';

export const CONFIG_SIDEBAR_KEYS = [
  'settings',
  'account',
  'seats',
  ...SETTINGS_SECTIONS,
] as const;

export function isConfigSidebarActive(active: string): boolean {
  return (CONFIG_SIDEBAR_KEYS as readonly string[]).includes(active);
}

type ConfigNavItem = {
  key: string;
  href: string;
  icon: string;
  /** Closed glyphs on the collapsed rail — outline at 18px reads as dots. */
  filled?: boolean;
  label: string;
  external?: boolean;
  destructive?: boolean;
};

const RAIL_FILLED: Partial<Record<SettingsSection, string>> = {
  profile: personCircle,
  language,
  permissions: key,
  tutorials: school,
};

interface Props {
  active: string;
}

export default function ConfigSidebarNav({ active }: Props) {
  const { t, i18n } = useTranslation(['web', 'settings']);
  const onMy = isMyWebHost();
  const locale = i18n.language;

  const outbound: Partial<Record<SettingsSection, ConfigNavItem[]>> = {
    about: [
      {
        key: 'helpCenter',
        href: getHelpUrl(locale),
        icon: helpCircle,
        filled: true,
        label: t('settings:helpCenter.label'),
        external: true,
      },
      {
        key: 'contactSupport',
        href: `mailto:${SUPPORT_EMAIL}`,
        icon: mail,
        filled: true,
        label: t('settings:contactSupport.label'),
      },
    ],
  };

  return (
    <nav aria-label={t('web:settings.sectionNav')}>
      <SidebarGroup>
        <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('web:nav.backToWork')}>
                  <a href={HOME_PATH}>
                    <IonIcon icon={arrowBackOutline} size={18} />
                    <span>{t('web:nav.backToWork')}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {SETTINGS_GROUPS.filter((group) => group.key !== 'legal' && group.key !== 'advanced').map(
        (group) => {
        const defs = SETTINGS_SECTION_DEFS.filter((def) => def.group === group.key);
        if (defs.length === 0) return null;

        const items: ConfigNavItem[] = defs.flatMap((def) => {
          const links = def.externalOnHub ? outbound[def.key] : undefined;
          if (links) return links;
          return [
            {
              key: def.key,
              href: settingsPath(def.key),
              icon: RAIL_FILLED[def.key] ?? def.icon,
              filled: !!RAIL_FILLED[def.key],
              label: t(def.titleKey),
              destructive: def.destructive,
            },
          ];
        });

        if (group.key === 'account' && onMy) {
          items.push(
            {
              key: 'account',
              href: '/account',
              icon: card,
              filled: true,
              label: t('web:nav.account'),
            },
            {
              key: 'seats',
              href: '/seats',
              icon: people,
              filled: true,
              label: t('web:nav.seats'),
            },
          );
        }

        const heading = t(group.titleKey);
        return (
          <SidebarGroup key={group.key}>
            <SidebarSeparator className="mx-0 mb-2" />
            <SidebarGroupLabel>{heading}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-2.5">
                {items.map((item) => {
                  const isActive = item.key === active;
                  return (
                    <SidebarMenuItem key={item.key}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.label}
                        className={
                          item.destructive && !isActive
                            ? 'text-destructive hover:text-destructive'
                            : undefined
                        }
                      >
                        <a
                          href={item.href}
                          aria-current={isActive ? 'page' : undefined}
                          {...(item.external
                            ? { target: '_blank', rel: 'noreferrer noopener' }
                            : null)}
                        >
                          <IonIcon icon={item.icon} size={20} filled={item.filled} />
                          <span>{item.label}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        );
      },
      )}
    </nav>
  );
}
